/**
 * Загрузка файла: зона перетаскивания и прогресс.
 *
 * Показываем скорость и оставшееся время, а не просто проценты.
 * При загрузке 10 ГБ разница принципиальная: «47%» ничего не говорит,
 * а «4.2 из 8.4 ГБ · 28 МБ/с · осталось 3 мин» — говорит всё.
 */
import { useRef, useState } from 'react';
import { Uploader, type UploadProgress } from '../lib/upload';
import { bytes, eta } from '../lib/format';
import { Progress, toast } from './ui';

/** Что показываем и что принимаем для каждого типа файла */
const KIND_UI = {
  MATERIAL: {
    accept: '.zip,.rar,.7z',
    hint: 'ZIP с исходниками · до 10 ГБ',
    done: 'Материал загружен',
  },
  FINAL: {
    accept: 'video/*,.mp4,.mov,.mkv',
    hint: 'MP4 · до 15 ГБ',
    done: 'Видео отправлено на проверку',
  },
  COVER: {
    accept: 'image/jpeg,image/png,image/webp',
    hint: 'JPG или PNG · до 20 МБ',
    done: 'Обложка загружена',
  },
} as const;

export function FileUploader({
  videoId,
  kind,
  onDone,
  compact,
}: {
  videoId: string;
  kind: 'MATERIAL' | 'FINAL' | 'COVER';
  onDone: () => void;
  /** Уменьшенный вид — для обложки, она не главное действие на экране */
  compact?: boolean;
}) {
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [dragging, setDragging] = useState(false);
  const uploaderRef = useRef<Uploader | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const ui = KIND_UI[kind];

  const upload = async (file: File) => {
    const uploader = new Uploader(file, videoId, kind, setProgress);
    uploaderRef.current = uploader;
    try {
      await uploader.start();
      toast(ui.done);
      onDone();
      setProgress(null);
    } catch {
      // Текст ошибки уже показан в прогрессе — дублировать не нужно
    } finally {
      uploaderRef.current = null;
    }
  };

  // --- идёт загрузка ---
  if (progress && progress.status !== 'done') {
    const failed = progress.status === 'error';

    return (
      <div className="card">
        <div className="flex justify-between items-center">
          <div className="font-semibold">
            {progress.status === 'preparing' && 'Подготовка…'}
            {progress.status === 'uploading' && 'Загрузка'}
            {progress.status === 'finishing' && 'Сборка файла…'}
            {failed && <span className="text-dang">Ошибка загрузки</span>}
          </div>
          {!failed && (
            <div className="mono font-extrabold text-accent2 text-base">
              {progress.percent.toFixed(1)}%
            </div>
          )}
        </div>

        <Progress percent={progress.percent} className="mt-3" smooth />

        {failed ? (
          <>
            <div className="text-[12.5px] text-dang mt-3 leading-snug">{progress.error}</div>
            <button className="btn btn-sm mt-3" onClick={() => setProgress(null)}>
              Попробовать снова
            </button>
          </>
        ) : (
          <>
            <div className="flex justify-between mt-2.5 text-[12.5px] text-muted2">
              <span className="mono">
                {bytes(progress.uploadedBytes)} из {bytes(progress.totalBytes)}
              </span>
              <span className="mono">
                {progress.speedMbps} МБ/с · осталось {eta(progress.etaSec)}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <div className="text-[11.5px] text-muted2 flex-1 leading-relaxed">
                Файл грузится напрямую в хранилище. Если связь оборвётся, загрузка
                продолжится с места обрыва — заново заливать не придётся.
              </div>
              <button
                className="btn btn-sm"
                onClick={() => {
                  uploaderRef.current?.abort();
                  setProgress(null);
                }}
              >
                Отменить
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // --- ожидание файла ---
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) void upload(file);
      }}
      onClick={() => inputRef.current?.click()}
      className={`border-[1.5px] border-dashed rounded-xl2 text-center cursor-pointer
                  transition-colors ${compact ? 'py-5 px-4' : 'py-9 px-5'} ${
                    dragging
                      ? 'border-accent bg-accent/10'
                      : 'border-[#33334A] bg-[#0D0D15] hover:border-accent hover:bg-accent/[0.06]'
                  }`}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={ui.accept}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void upload(file);
          e.target.value = '';
        }}
      />
      <div className={compact ? 'text-xl opacity-75 mb-1.5' : 'text-3xl opacity-75 mb-2.5'}>⤒</div>
      <div className={`font-semibold mb-1.5 ${compact ? 'text-[13px]' : 'text-[14.5px]'}`}>
        {compact ? 'Загрузить обложку' : 'Перетащи файл сюда или нажми, чтобы выбрать'}
      </div>
      <div className="text-xs text-muted2">
        {ui.hint}
        {kind !== 'COVER' && ' · загрузка с докачкой при обрыве'}
      </div>
    </div>
  );
}
